import React, { useRef, useState } from "react";
import { UploadCloud, File as FileIcon, X, Loader2 } from "lucide-react";
import { uploadFile } from "../../lib/api";
import { toast } from "sonner";

export const FileUpload = ({ value = [], onChange, maxMb = 10, testid = "file-upload" }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFiles = async (files) => {
    for (const file of Array.from(files)) {
      if (file.size > maxMb * 1024 * 1024) {
        toast.error(`${file.name} exceeds the ${maxMb} MB limit.`);
        continue;
      }
      setBusy(true);
      try {
        const res = await uploadFile(file);
        onChange([...(value || []), res]);
        toast.success(`Uploaded ${res.filename}`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Upload failed.");
      } finally {
        setBusy(false);
      }
    }
  };

  const remove = (id) => onChange((value || []).filter((f) => f.id !== id));

  return (
    <div data-testid={testid}>
      <div
        onClick={() => !busy && inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          handleFiles(e.dataTransfer.files);
        }}
        role="button"
        aria-label="Upload files"
        className="border border-dashed border-brand-gold/70 bg-white p-6 text-center cursor-pointer hover:bg-accent/40 transition-colors duration-200"
        data-testid={`${testid}-dropzone`}
      >
        {busy ? (
          <Loader2 className="mx-auto h-6 w-6 text-brand-gold animate-spin" strokeWidth={1.5} />
        ) : (
          <UploadCloud className="mx-auto h-6 w-6 text-brand-gold" strokeWidth={1.5} />
        )}
        <p className="mt-2 text-sm text-brand-black font-medium">Click to upload or drag and drop</p>
        <p className="text-xs text-muted-foreground mt-1">
          PDF, Word, JPEG or PNG · Maximum {maxMb} MB
        </p>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          multiple
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.heic"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid={`${testid}-input`}
        />
      </div>
      {(value || []).length > 0 && (
        <ul className="mt-3 space-y-2">
          {value.map((f) => (
            <li
              key={f.id}
              className="flex items-center justify-between border border-border bg-white px-3 py-2 text-sm"
              data-testid={`${testid}-item-${f.id}`}
            >
              <span className="flex items-center gap-2 truncate">
                <FileIcon className="h-4 w-4 text-brand-gold shrink-0" strokeWidth={1.5} />
                <span className="truncate">{f.filename}</span>
              </span>
              <button
                type="button"
                onClick={() => remove(f.id)}
                className="text-muted-foreground hover:text-brand-danger transition-colors"
                aria-label={`Remove ${f.filename}`}
                data-testid={`${testid}-remove-${f.id}`}
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
